/**
 * Menu allergen / additive / dietary tag registry.
 *
 * Source priority (every entry):
 * 1. German national — BMEL, gesetze-im-internet.de (LMZDV), LAVES Merkblätter
 * 2. EU — EUR-Lex VO (EU) 1169/2011 Anhang II, VO (EG) 1333/2008, …
 *
 * Verified against EUR-Lex CELEX:32011R1169 Annex II and LMZDV §5 (2026-07).
 * Do not invent IDs — extend only from cited legal texts.
 */

export type MenuTagKind = 'allergen' | 'additive' | 'dietary';

export interface MenuLegalBasis {
  text: string;
  sourceUri: string;
}

export interface MenuTagDefinition {
  id: string;
  kind: MenuTagKind;
  labelDe: string;
  labelEn: string;
  /** Lucide icon name; null = text-only (additives). */
  iconKey: string | null;
  legalBasisDe: MenuLegalBasis;
  legalBasisEu?: MenuLegalBasis & { annexItemNumber?: number };
  codexLetter?: string;
  gastronomyShorthand?: number;
  claimType?: 'regulated' | 'voluntary';
}

const BMEL_LMIV =
  'https://www.bmleh.de/DE/themen/ernaehrung/lebensmittel-kennzeichnung/pflichtangaben/lebensmittelkennzeichnung-wichtigsten-vorgaben-lmiv.html';
const EURLEX_1169 =
  'https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:32011R1169';
const LMZDV_5 = 'https://www.gesetze-im-internet.de/lmzdv/__5.html';
const LAVES_MERKBLATT =
  'https://www.laves.niedersachsen.de/download/92611/Kennzeichnung_von_Zusatzstoffen_Allergenen_und_weiteren_Lebensmittelinhaltsstoffen_in_Gastronomie_und_Gemeinschaftsverpflegung_PDF_nicht_barrierefrei_.pdf';

export const MENU_ALLERGEN_TAGS: readonly MenuTagDefinition[] = [
  {
    id: 'cereals_containing_gluten',
    kind: 'allergen',
    labelDe: 'Glutenhaltiges Getreide',
    labelEn: 'Cereals containing gluten',
    iconKey: 'Wheat',
    codexLetter: 'A',
    legalBasisDe: { text: 'LMIV / BMEL Pflichtangaben — Allergene', sourceUri: BMEL_LMIV },
    legalBasisEu: {
      text: 'VO (EU) 1169/2011 Anhang II Nr. 1',
      sourceUri: EURLEX_1169,
      annexItemNumber: 1,
    },
  },
  {
    id: 'crustaceans',
    kind: 'allergen',
    labelDe: 'Krebstiere',
    labelEn: 'Crustaceans',
    iconKey: 'Shrimp',
    codexLetter: 'B',
    legalBasisDe: { text: 'LMIV / BMEL Pflichtangaben — Allergene', sourceUri: BMEL_LMIV },
    legalBasisEu: {
      text: 'VO (EU) 1169/2011 Anhang II Nr. 2',
      sourceUri: EURLEX_1169,
      annexItemNumber: 2,
    },
  },
  {
    id: 'eggs',
    kind: 'allergen',
    labelDe: 'Eier',
    labelEn: 'Eggs',
    iconKey: 'Egg',
    codexLetter: 'C',
    legalBasisDe: { text: 'LMIV / BMEL Pflichtangaben — Allergene', sourceUri: BMEL_LMIV },
    legalBasisEu: {
      text: 'VO (EU) 1169/2011 Anhang II Nr. 3',
      sourceUri: EURLEX_1169,
      annexItemNumber: 3,
    },
  },
  {
    id: 'fish',
    kind: 'allergen',
    labelDe: 'Fisch',
    labelEn: 'Fish',
    iconKey: 'Fish',
    codexLetter: 'D',
    legalBasisDe: { text: 'LMIV / BMEL Pflichtangaben — Allergene', sourceUri: BMEL_LMIV },
    legalBasisEu: {
      text: 'VO (EU) 1169/2011 Anhang II Nr. 4',
      sourceUri: EURLEX_1169,
      annexItemNumber: 4,
    },
  },
  {
    id: 'peanuts',
    kind: 'allergen',
    labelDe: 'Erdnüsse',
    labelEn: 'Peanuts',
    iconKey: 'Nut',
    codexLetter: 'E',
    legalBasisDe: { text: 'LMIV / BMEL Pflichtangaben — Allergene', sourceUri: BMEL_LMIV },
    legalBasisEu: {
      text: 'VO (EU) 1169/2011 Anhang II Nr. 5',
      sourceUri: EURLEX_1169,
      annexItemNumber: 5,
    },
  },
  {
    id: 'soybeans',
    kind: 'allergen',
    labelDe: 'Sojabohnen',
    labelEn: 'Soybeans',
    iconKey: 'Bean',
    codexLetter: 'F',
    legalBasisDe: { text: 'LMIV / BMEL Pflichtangaben — Allergene', sourceUri: BMEL_LMIV },
    legalBasisEu: {
      text: 'VO (EU) 1169/2011 Anhang II Nr. 6',
      sourceUri: EURLEX_1169,
      annexItemNumber: 6,
    },
  },
  {
    id: 'milk',
    kind: 'allergen',
    labelDe: 'Milch (einschl. Laktose)',
    labelEn: 'Milk (including lactose)',
    iconKey: 'Milk',
    codexLetter: 'G',
    legalBasisDe: { text: 'LMIV / BMEL Pflichtangaben — Allergene', sourceUri: BMEL_LMIV },
    legalBasisEu: {
      text: 'VO (EU) 1169/2011 Anhang II Nr. 7',
      sourceUri: EURLEX_1169,
      annexItemNumber: 7,
    },
  },
  {
    id: 'nuts',
    kind: 'allergen',
    labelDe: 'Schalenfrüchte',
    labelEn: 'Tree nuts',
    iconKey: 'Nut',
    codexLetter: 'H',
    legalBasisDe: { text: 'LMIV / BMEL Pflichtangaben — Allergene', sourceUri: BMEL_LMIV },
    legalBasisEu: {
      text: 'VO (EU) 1169/2011 Anhang II Nr. 8',
      sourceUri: EURLEX_1169,
      annexItemNumber: 8,
    },
  },
  {
    id: 'celery',
    kind: 'allergen',
    labelDe: 'Sellerie',
    labelEn: 'Celery',
    iconKey: 'LeafyGreen',
    codexLetter: 'L',
    legalBasisDe: { text: 'LMIV / BMEL Pflichtangaben — Allergene', sourceUri: BMEL_LMIV },
    legalBasisEu: {
      text: 'VO (EU) 1169/2011 Anhang II Nr. 9',
      sourceUri: EURLEX_1169,
      annexItemNumber: 9,
    },
  },
  {
    id: 'mustard',
    kind: 'allergen',
    labelDe: 'Senf',
    labelEn: 'Mustard',
    iconKey: 'Droplets',
    codexLetter: 'M',
    legalBasisDe: { text: 'LMIV / BMEL Pflichtangaben — Allergene', sourceUri: BMEL_LMIV },
    legalBasisEu: {
      text: 'VO (EU) 1169/2011 Anhang II Nr. 10',
      sourceUri: EURLEX_1169,
      annexItemNumber: 10,
    },
  },
  {
    id: 'sesame_seeds',
    kind: 'allergen',
    labelDe: 'Sesamsamen',
    labelEn: 'Sesame seeds',
    iconKey: 'CircleDot',
    codexLetter: 'N',
    legalBasisDe: { text: 'LMIV / BMEL Pflichtangaben — Allergene', sourceUri: BMEL_LMIV },
    legalBasisEu: {
      text: 'VO (EU) 1169/2011 Anhang II Nr. 11',
      sourceUri: EURLEX_1169,
      annexItemNumber: 11,
    },
  },
  {
    id: 'sulphur_dioxide_sulphites',
    kind: 'allergen',
    labelDe: 'Schwefeldioxid und Sulfite',
    labelEn: 'Sulphur dioxide and sulphites',
    iconKey: 'FlaskConical',
    codexLetter: 'O',
    legalBasisDe: { text: 'LMIV / BMEL Pflichtangaben — Allergene', sourceUri: BMEL_LMIV },
    legalBasisEu: {
      text: 'VO (EU) 1169/2011 Anhang II Nr. 12',
      sourceUri: EURLEX_1169,
      annexItemNumber: 12,
    },
  },
  {
    id: 'lupin',
    kind: 'allergen',
    labelDe: 'Lupinen',
    labelEn: 'Lupin',
    iconKey: 'Flower2',
    codexLetter: 'P',
    legalBasisDe: { text: 'LMIV / BMEL Pflichtangaben — Allergene', sourceUri: BMEL_LMIV },
    legalBasisEu: {
      text: 'VO (EU) 1169/2011 Anhang II Nr. 13',
      sourceUri: EURLEX_1169,
      annexItemNumber: 13,
    },
  },
  {
    id: 'molluscs',
    kind: 'allergen',
    labelDe: 'Weichtiere',
    labelEn: 'Molluscs',
    iconKey: 'Shell',
    codexLetter: 'R',
    legalBasisDe: { text: 'LMIV / BMEL Pflichtangaben — Allergene', sourceUri: BMEL_LMIV },
    legalBasisEu: {
      text: 'VO (EU) 1169/2011 Anhang II Nr. 14',
      sourceUri: EURLEX_1169,
      annexItemNumber: 14,
    },
  },
] as const;

/** LMZDV §5 class declarations — text-only (no icons). Gastronomy shorthand 1–n from LAVES. */
export const MENU_ADDITIVE_TAGS: readonly MenuTagDefinition[] = [
  {
    id: 'colorant',
    kind: 'additive',
    labelDe: 'mit Farbstoff',
    labelEn: 'with colour',
    iconKey: null,
    gastronomyShorthand: 1,
    legalBasisDe: { text: 'LMZDV §5 — Farbstoffe', sourceUri: LMZDV_5 },
  },
  {
    id: 'preservative',
    kind: 'additive',
    labelDe: 'mit Konservierungsstoff',
    labelEn: 'with preservative',
    iconKey: null,
    gastronomyShorthand: 2,
    legalBasisDe: { text: 'LMZDV §5 — Konservierungsstoffe', sourceUri: LMZDV_5 },
  },
  {
    id: 'antioxidant',
    kind: 'additive',
    labelDe: 'mit Antioxidationsmittel',
    labelEn: 'with antioxidant',
    iconKey: null,
    gastronomyShorthand: 3,
    legalBasisDe: { text: 'LMZDV §5 — Antioxidationsmittel', sourceUri: LMZDV_5 },
  },
  {
    id: 'flavour_enhancer',
    kind: 'additive',
    labelDe: 'mit Geschmacksverstärker',
    labelEn: 'with flavour enhancer',
    iconKey: null,
    gastronomyShorthand: 4,
    legalBasisDe: { text: 'LMZDV §5 — Geschmacksverstärker', sourceUri: LMZDV_5 },
  },
  {
    id: 'sulphur_treated',
    kind: 'additive',
    labelDe: 'geschwefelt',
    labelEn: 'sulphur dioxide treated',
    iconKey: null,
    gastronomyShorthand: 5,
    legalBasisDe: { text: 'LMZDV §5 / LAVES Merkblatt', sourceUri: LAVES_MERKBLATT },
  },
  {
    id: 'blackened',
    kind: 'additive',
    labelDe: 'geschwärzt',
    labelEn: 'blackened',
    iconKey: null,
    gastronomyShorthand: 6,
    legalBasisDe: { text: 'LMZDV §5 — Eisensalze', sourceUri: LMZDV_5 },
  },
  {
    id: 'waxed',
    kind: 'additive',
    labelDe: 'gewachst',
    labelEn: 'waxed',
    iconKey: null,
    gastronomyShorthand: 7,
    legalBasisDe: { text: 'LMZDV §5 — Oberflächenbehandlung', sourceUri: LMZDV_5 },
  },
  {
    id: 'phosphate',
    kind: 'additive',
    labelDe: 'mit Phosphat',
    labelEn: 'with phosphate',
    iconKey: null,
    gastronomyShorthand: 8,
    legalBasisDe: { text: 'LMZDV §5 — Phosphate (Fleisch)', sourceUri: LMZDV_5 },
  },
  {
    id: 'sweetener',
    kind: 'additive',
    labelDe: 'mit Süßungsmittel(n)',
    labelEn: 'with sweetener(s)',
    iconKey: null,
    gastronomyShorthand: 9,
    legalBasisDe: { text: 'LMZDV §5 — Süßungsmittel', sourceUri: LMZDV_5 },
  },
  {
    id: 'phenylalanine',
    kind: 'additive',
    labelDe: 'enthält eine Phenylalaninquelle',
    labelEn: 'contains a source of phenylalanine',
    iconKey: null,
    gastronomyShorthand: 10,
    legalBasisDe: { text: 'LMZDV §5 — Aspartam', sourceUri: LMZDV_5 },
  },
] as const;

export const MENU_DIETARY_TAGS: readonly MenuTagDefinition[] = [
  {
    id: 'organic',
    kind: 'dietary',
    labelDe: 'Bio',
    labelEn: 'Organic',
    iconKey: 'Leaf',
    claimType: 'regulated',
    legalBasisDe: { text: 'BMEL Bio-Kennzeichnung', sourceUri: BMEL_LMIV },
    legalBasisEu: {
      text: 'VO (EU) 2018/848',
      sourceUri: 'https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:32018R0848',
    },
  },
  {
    id: 'gluten_free',
    kind: 'dietary',
    labelDe: 'Glutenfrei',
    labelEn: 'Gluten-free',
    iconKey: 'WheatOff',
    claimType: 'regulated',
    legalBasisDe: { text: 'BMEL Kennzeichnung glutenfrei', sourceUri: BMEL_LMIV },
    legalBasisEu: {
      text: 'VO (EU) Nr. 828/2014',
      sourceUri: 'https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:32014R0828',
    },
  },
  {
    id: 'vegan',
    kind: 'dietary',
    labelDe: 'Vegan',
    labelEn: 'Vegan',
    iconKey: 'Sprout',
    claimType: 'voluntary',
    legalBasisDe: { text: 'Freiwillige Angabe', sourceUri: BMEL_LMIV },
  },
  {
    id: 'vegetarian',
    kind: 'dietary',
    labelDe: 'Vegetarisch',
    labelEn: 'Vegetarian',
    iconKey: 'Salad',
    claimType: 'voluntary',
    legalBasisDe: { text: 'Freiwillige Angabe', sourceUri: BMEL_LMIV },
  },
  {
    id: 'halal',
    kind: 'dietary',
    labelDe: 'Halal',
    labelEn: 'Halal',
    iconKey: 'BadgeCheck',
    claimType: 'voluntary',
    legalBasisDe: { text: 'Freiwillige Angabe / Zertifizierung', sourceUri: BMEL_LMIV },
  },
  {
    id: 'lactose_free',
    kind: 'dietary',
    labelDe: 'Laktosefrei',
    labelEn: 'Lactose-free',
    iconKey: 'MilkOff',
    claimType: 'voluntary',
    legalBasisDe: { text: 'Freiwillige Angabe', sourceUri: BMEL_LMIV },
  },
] as const;

export const MENU_TAG_REGISTRY: readonly MenuTagDefinition[] = [
  ...MENU_ALLERGEN_TAGS,
  ...MENU_ADDITIVE_TAGS,
  ...MENU_DIETARY_TAGS,
];

const byId = new Map(MENU_TAG_REGISTRY.map((tag) => [tag.id, tag]));

export const MENU_ALLERGEN_IDS = MENU_ALLERGEN_TAGS.map((t) => t.id);
export const MENU_DIETARY_IDS = MENU_DIETARY_TAGS.map((t) => t.id);
export const MENU_ADDITIVE_IDS = MENU_ADDITIVE_TAGS.map((t) => t.id);

export function getMenuTagDefinition(id: string): MenuTagDefinition | undefined {
  return byId.get(id);
}

export function getMenuTagLabel(tag: MenuTagDefinition, locale: string): string {
  const base = locale.split('-')[0]?.toLowerCase() ?? locale;
  if (base === 'de') {
    return tag.labelDe;
  }
  return tag.labelEn;
}

/**
 * Editor / legend label: gastronomy code first (A–R allergens, 1–n additives), then name.
 * Dietary tags have no code and return the plain localized name.
 */
export function getMenuTagOptionLabel(tag: MenuTagDefinition, locale: string): string {
  const name = getMenuTagLabel(tag, locale);
  if (tag.codexLetter) {
    return `${tag.codexLetter} · ${name}`;
  }
  if (tag.gastronomyShorthand != null) {
    return `${tag.gastronomyShorthand} · ${name}`;
  }
  return name;
}

/** Compact chip token: letter/number when present, otherwise the localized name. */
export function getMenuTagCodeLabel(tag: MenuTagDefinition, locale: string): string {
  if (tag.codexLetter) {
    return tag.codexLetter;
  }
  if (tag.gastronomyShorthand != null) {
    return String(tag.gastronomyShorthand);
  }
  return getMenuTagLabel(tag, locale);
}

export interface MenuLegalMark {
  /** Guest-facing superscript token (allergen letter or additive number). */
  code: string;
  /** Full localized label for tooltip / aria. */
  label: string;
  kind: 'allergen' | 'additive';
}

/**
 * Gastronomy Speisekarte marks for a dish: allergen letters (A–R) then additive
 * numbers (1–n). Sorted for stable print order.
 */
export function resolveMenuItemLegalMarks(
  allergenIds: string[] | undefined,
  additiveIds: string[] | undefined,
  locale: string,
): MenuLegalMark[] {
  const marks: MenuLegalMark[] = [];
  for (const id of allergenIds ?? []) {
    const def = byId.get(id);
    if (!def || def.kind !== 'allergen' || !def.codexLetter) {
      continue;
    }
    marks.push({
      code: def.codexLetter,
      label: getMenuTagLabel(def, locale),
      kind: 'allergen',
    });
  }
  for (const id of additiveIds ?? []) {
    const def = byId.get(id);
    if (!def || def.kind !== 'additive' || def.gastronomyShorthand == null) {
      continue;
    }
    marks.push({
      code: String(def.gastronomyShorthand),
      label: getMenuTagLabel(def, locale),
      kind: 'additive',
    });
  }
  marks.sort((a, b) => {
    if (a.kind !== b.kind) {
      return a.kind === 'allergen' ? -1 : 1;
    }
    if (a.kind === 'allergen') {
      return a.code.localeCompare(b.code);
    }
    return Number(a.code) - Number(b.code);
  });
  return marks;
}

/** Joined superscript text e.g. `A,D,1` — empty when no legal marks. */
export function formatMenuItemLegalSuperscript(
  allergenIds: string[] | undefined,
  additiveIds: string[] | undefined,
  locale: string,
): string {
  return resolveMenuItemLegalMarks(allergenIds, additiveIds, locale)
    .map((mark) => mark.code)
    .join(',');
}

export function hydrateMenuTagRefs(
  ids: string[] | undefined,
  locale: string,
): import('./menuTagRef').MenuTagRef[] {
  if (!ids?.length) {
    return [];
  }
  const refs: import('./menuTagRef').MenuTagRef[] = [];
  for (const id of ids) {
    const def = byId.get(id);
    if (!def) {
      const additive = /^legend_additive_(.+)$/i.exec(id);
      if (additive?.[1]) {
        refs.push({ id, iconKey: null, label: additive[1] });
        continue;
      }
      const allergen = /^legend_allergen_(.+)$/i.exec(id);
      if (allergen?.[1]) {
        refs.push({ id, iconKey: null, label: allergen[1].toUpperCase() });
        continue;
      }
      const dietary = /^legend_dietary_(.+)$/i.exec(id);
      if (dietary?.[1]) {
        refs.push({
          id,
          iconKey: null,
          label: dietary[1].replace(/_/g, ' ').trim() || id,
        });
        continue;
      }
      refs.push({ id, iconKey: null, label: id });
      continue;
    }
    refs.push({
      id: def.id,
      iconKey: def.iconKey,
      label: getMenuTagLabel(def, locale),
    });
  }
  return refs;
}

/** Map gastronomy footnote number (1–n) → additive registry id. */
export function resolveGastronomyShorthand(num: number): string | undefined {
  return MENU_ADDITIVE_TAGS.find((t) => t.gastronomyShorthand === num)?.id;
}

/**
 * Resolve a free-form token (registry id, Annex II letter, footnote number, or DE/EN label)
 * to a known menu tag id.
 */
export function resolveMenuTagId(token: string): string | undefined {
  const raw = token.trim();
  if (!raw) return undefined;

  // Bilingual legend labels e.g. "Ei / Egg" or "Sesam / Sesame"
  if (/[/,|;]/.test(raw) || /\s+\/\s+/.test(raw)) {
    const parts = raw
      .split(/\s*\/\s*|\s*[|,;]\s*/)
      .map((part) => part.trim())
      .filter(Boolean);
    for (const part of parts) {
      const hit = resolveMenuTagId(part);
      if (hit) return hit;
    }
  }

  // "Farbstoffe (Coloring)" / "Phosphat (Phosphate)" — DE name + EN gloss
  const paren = raw.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (paren?.[1] && paren[2]) {
    for (const part of [paren[1], paren[2]]) {
      const hit = resolveMenuTagId(part.trim());
      if (hit) return hit;
    }
  }

  const lower = raw.toLowerCase();
  if (byId.has(raw)) return raw;
  if (byId.has(lower)) return lower;

  const compact = lower.replace(/[^a-z0-9äöüß]+/gi, '');
  // Prefer named aliases before Annex/LMZDV index codes — restaurant legends
  // often reuse 1–n / A–R with local meanings.
  const aliases: Record<string, string> = {
    egg: 'eggs',
    ei: 'eggs',
    eier: 'eggs',
    gluten: 'cereals_containing_gluten',
    weizen: 'cereals_containing_gluten',
    milch: 'milk',
    lactose: 'milk',
    laktose: 'milk',
    soya: 'soybeans',
    soja: 'soybeans',
    soy: 'soybeans',
    peanut: 'peanuts',
    peanuts: 'peanuts',
    erdnuss: 'peanuts',
    erdnüsse: 'peanuts',
    fish: 'fish',
    fisch: 'fish',
    mustard: 'mustard',
    senf: 'mustard',
    celery: 'celery',
    sellerie: 'celery',
    sesame: 'sesame_seeds',
    sesam: 'sesame_seeds',
    shellfish: 'crustaceans',
    schalentiere: 'crustaceans',
    crustaceans: 'crustaceans',
    molluscs: 'molluscs',
    mollusks: 'molluscs',
    weichtiere: 'molluscs',
    nuts: 'nuts',
    nut: 'nuts',
    schalenfrüchte: 'nuts',
    lupin: 'lupin',
    lupine: 'lupin',
    lupinen: 'lupin',
    sulphites: 'sulphur_dioxide_sulphites',
    sulfites: 'sulphur_dioxide_sulphites',
    sulfit: 'sulphur_dioxide_sulphites',
    sulfite: 'sulphur_dioxide_sulphites',
    farbstoff: 'colorant',
    farbstoffe: 'colorant',
    coloring: 'colorant',
    colouring: 'colorant',
    colour: 'colorant',
    konservierungsstoff: 'preservative',
    konservierungsstoffe: 'preservative',
    preservatives: 'preservative',
    preservative: 'preservative',
    antioxidantien: 'antioxidant',
    antioxidants: 'antioxidant',
    antioxidant: 'antioxidant',
    antioxidantionsmittel: 'antioxidant',
    antioxidationsmittel: 'antioxidant',
    geschmacksverstaerker: 'flavour_enhancer',
    geschmacksverstärker: 'flavour_enhancer',
    flavorenhancers: 'flavour_enhancer',
    flavourenhancers: 'flavour_enhancer',
    flavorenhancer: 'flavour_enhancer',
    geschwefelt: 'sulphur_treated',
    sulfured: 'sulphur_treated',
    sulphured: 'sulphur_treated',
    geschwärzt: 'blackened',
    blackened: 'blackened',
    phosphat: 'phosphate',
    phosphate: 'phosphate',
    süßungsmittel: 'sweetener',
    sussungsmittel: 'sweetener',
    sweeteners: 'sweetener',
    sweetener: 'sweetener',
    gewachst: 'waxed',
    waxed: 'waxed',
    phenylalanin: 'phenylalanine',
    phenylalaninquelle: 'phenylalanine',
    phenylalanine: 'phenylalanine',
  };
  const aliasHit = aliases[lower] ?? aliases[compact];
  if (aliasHit) return aliasHit;

  if (/^[a-z]$/i.test(raw)) {
    const letter = raw.toUpperCase();
    const allergen = MENU_ALLERGEN_TAGS.find((t) => t.codexLetter === letter);
    if (allergen) return allergen.id;
  }

  if (/^\d{1,2}$/.test(raw)) {
    const additiveId = resolveGastronomyShorthand(Number.parseInt(raw, 10));
    if (additiveId) return additiveId;
  }

  for (const tag of MENU_TAG_REGISTRY) {
    const de = tag.labelDe.toLowerCase().replace(/[^a-z0-9äöüß]+/gi, '');
    const en = tag.labelEn.toLowerCase().replace(/[^a-z0-9]+/gi, '');
    if (compact && (compact === de || compact === en)) {
      return tag.id;
    }
    // Partial contains for long labels ("Glutenhaltiges Getreide" vs "Gluten")
    if (compact.length >= 4 && (de.includes(compact) || en.includes(compact))) {
      return tag.id;
    }
  }

  return undefined;
}

/**
 * Split a mixed dietary/additive id list (legacy storage often folded additives into dietaryTags).
 */
export function partitionDietaryAndAdditiveIds(ids: readonly string[]): {
  dietary: string[];
  additive: string[];
  other: string[];
} {
  const dietaryIdSet = new Set<string>(MENU_DIETARY_IDS);
  const additiveIdSet = new Set<string>(MENU_ADDITIVE_IDS);
  const dietary: string[] = [];
  const additive: string[] = [];
  const other: string[] = [];
  for (const id of ids) {
    const trimmed = id.trim();
    if (!trimmed) continue;
    if (dietaryIdSet.has(trimmed)) {
      dietary.push(trimmed);
    } else if (additiveIdSet.has(trimmed) || trimmed.startsWith('legend_additive_')) {
      additive.push(trimmed);
    } else {
      other.push(trimmed);
    }
  }
  return { dietary, additive, other };
}

/** Merge explicit additive ids with any additives still embedded in dietaryTags. */
export function normalizeMenuItemTagIds(input: {
  allergenIds?: readonly string[];
  additiveIds?: readonly string[];
  dietaryTagIds?: readonly string[];
}): {
  allergenIds: string[];
  additiveIds: string[];
  dietaryTagIds: string[];
} {
  const partitioned = partitionDietaryAndAdditiveIds(input.dietaryTagIds ?? []);
  const additiveSeen = new Set<string>();
  const additiveIds: string[] = [];
  for (const id of [...(input.additiveIds ?? []), ...partitioned.additive]) {
    if (additiveSeen.has(id)) continue;
    additiveSeen.add(id);
    additiveIds.push(id);
  }
  return {
    allergenIds: [...(input.allergenIds ?? [])],
    additiveIds,
    dietaryTagIds: [...partitioned.dietary, ...partitioned.other],
  };
}
